// repricePairToLastPrice keeps the PairPage header live with trades. It reprices the
// TON side (last_price/price_ton) by the new/old ratio. It deliberately
// does NOT touch price_usd/mcap_usd/fdv_usd: the wt-indexer trade event now carries those
// AUTHORITATIVELY (== REST, FRONTEND-WS-MCAP), so the caller sets them directly from the
// event. The old ratio-scaling of USD/mcap froze the TON/USD rate from the last seed and
// was the mcap drift; removing it makes live == REST on refresh (no reconstruction).
// Rolling change windows also stay untouched: only a backend response-wide snapshot
// knows their actual baselines, so a live trade cannot reconstruct them from a prior %.
// A non-positive prior last_price can't form a ratio, so the fields are left as-is and
// only last_price advances. Volume/txns/liquidity refresh on the REST seed.

import type { PairDetail, PriceWindowSnapshot, TradeSummary } from '../api/types'
import { tradeWindowUsdPrice } from './surfaceTrades'
import { scaledValuation, tradeUsdPrice } from './tonQuote'

export function repricePairToLastPrice(pair: PairDetail, lastPrice: number): PairDetail {
  const prevLp = pair.last_price
  const next: PairDetail = { ...pair, last_price: lastPrice }
  if (!Number.isFinite(prevLp) || prevLp <= 0 || !Number.isFinite(lastPrice)) {
    return next
  }
  const k = lastPrice / prevLp
  if (typeof pair.price_ton === 'number') next.price_ton = pair.price_ton * k
  // price_usd/mcap_usd/fdv_usd are NOT scaled here — they arrive authoritatively on the
  // trade event (FRONTEND-WS-MCAP) and the caller sets them. Ratio-scaling them froze the
  // TON/USD rate from the seed and was the mcap drift.

  return next
}

// applyLiveTrade folds one live WS trade onto the cached pair: it reprices the TON side
// off the candle price, then takes the event's AUTHORITATIVE price_usd / mcap_usd
// (== REST) and bonding_progress_pct DIRECTLY — no reconstruction (FRONTEND-WS-MCAP). Fields
// the event omits (nil) are left as-is. Pure.
export function applyLiveTrade(prev: PairDetail, t: TradeSummary, candlePrice: number): PairDetail {
  const next = candlePrice > 0 ? repricePairToLastPrice(prev, candlePrice) : { ...prev }
  if (Number.isFinite(t.ts)) next.price_source_ts = t.ts
  if (typeof t.lt === 'number' && Number.isFinite(t.lt)) next.price_source_lt = t.lt
  else next.price_source_lt = undefined
  // A stamped price_usd is verbatim (execution × rate — TON/USD₮ unchanged);
  // a non-TON-quoted trade has none, so its own stamped ton_usd_rate prices it.
  const usdPrice = Number.isFinite(t.price_usd) ? (t.price_usd as number) : tradeUsdPrice(t)
  if (usdPrice != null) next.price_usd = usdPrice
  // A non-TON trade carries no mcap/fdv envelope either: scale the seed
  // valuation by the live USD price move (scaledPairValuation twin), never
  // TON/USD. price_usd and the valuation update off the SAME prev values so
  // the ratio stays consistent across consecutive trades.
  if (typeof t.mcap_usd === 'number' && Number.isFinite(t.mcap_usd)) {
    next.mcap_usd = t.mcap_usd
  } else {
    const scaled = scaledValuation(t, usdPrice, prev.price_usd, prev.mcap_usd)
    if (scaled != null) next.mcap_usd = scaled
  }
  if (typeof t.fdv_usd === 'number' && Number.isFinite(t.fdv_usd)) {
    next.fdv_usd = t.fdv_usd
  } else {
    const scaled = scaledValuation(t, usdPrice, prev.price_usd, prev.fdv_usd)
    if (scaled != null) next.fdv_usd = scaled
  }
  // Server-side monotonic/cumulative fields (ATH, network-fees total): take the
  // event's value but never regress below what the page already shows (guards
  // WS out-of-order delivery; TOKEN-ATH / TOKEN-NET-FEES). A birth ATH is the
  // one exception: the first trade replaces it (foldTradeAth, ATH-BIRTH).
  Object.assign(next, foldTradeAth(prev, t.ath_mcap_usd))
  next.ath_mcap_usd = athAtLeastMcap(next.ath_mcap_usd, next.mcap_usd)
  next.net_fees_gram = monotonicMax(prev.net_fees_gram, t.net_fees_gram)
  // Liquidity cell advances per-trade (live == REST): take the event's authoritative
  // liquidity_usd directly. LP add/remove events move it between trades (usePairFeed).
  if (typeof t.liquidity_usd === 'number' && Number.isFinite(t.liquidity_usd))
    next.liquidity_usd = t.liquidity_usd
  if (
    /^[1-9][0-9]*$/.test(t.reserve_base_raw ?? '') &&
    /^[1-9][0-9]*$/.test(t.reserve_quote_raw ?? '')
  ) {
    next.reserve_base_raw = t.reserve_base_raw
    next.reserve_quote_raw = t.reserve_quote_raw
  }
  if (typeof t.bonding_progress_pct === 'number' && Number.isFinite(t.bonding_progress_pct))
    next.bonding_progress_pct = t.bonding_progress_pct
  // The backend snapshot already carries the exact, auditable denominator for
  // every window. A live trade only changes the numerator, so recalculate from
  // that frozen baseline immediately instead of hiding the value until a poll.
  return advancePairPriceWindows(next, t, candlePrice)
}

export function advancePairPriceWindows(
  pair: PairDetail,
  trade: TradeSummary,
  nativePrice?: number
): PairDetail {
  const next: PairDetail = { ...pair }
  if (!next.price_windows) return next
  next.price_windows = Object.fromEntries(
    Object.entries(next.price_windows).map(([key, window]) => [
      key,
      advancePriceWindow(window, trade, nativePrice),
    ])
  )
  next.price_change_5m_pct = next.price_windows['5m']?.pct ?? next.price_change_5m_pct
  next.price_change_1h_pct = next.price_windows['1h']?.pct ?? next.price_change_1h_pct
  next.price_change_6h_pct = next.price_windows['6h']?.pct ?? next.price_change_6h_pct
  next.price_change_24h_pct = next.price_windows['24h']?.pct ?? next.price_change_24h_pct
  return next
}

export function advancePriceWindow(
  window: PriceWindowSnapshot,
  trade: TradeSummary,
  nativePrice?: number
): PriceWindowSnapshot {
  const next: PriceWindowSnapshot = { ...window }
  // The window's USD current is a series point — stamped price_usd when the
  // frame carries it, else the CANDLE basis × the trade's own stamped rate
  // for a non-TON quote. Pending and unpriced frames stay null.
  const usdCurrent = tradeWindowUsdPrice(trade, nativePrice)
  const hasUSD = usdCurrent != null
  const hasNative =
    typeof nativePrice === 'number' && Number.isFinite(nativePrice) && nativePrice > 0
  if (
    (window.pct_denom === 'usd' && !hasUSD) ||
    (window.pct_denom === 'native' && !hasNative) ||
    (!window.pct_denom && !hasUSD && !hasNative)
  ) {
    return next
  }
  const priorCurrentFX =
    window.pct_denom === 'native' &&
    hasNative &&
    typeof window.current_price === 'number' &&
    Number.isFinite(window.current_price) &&
    window.current_price > 0 &&
    typeof window.current_native_price === 'number' &&
    Number.isFinite(window.current_native_price) &&
    window.current_native_price > 0
      ? window.current_price / window.current_native_price
      : null
  if (Number.isFinite(trade.ts)) {
    next.as_of = new Date(Math.max(Date.parse(window.as_of), trade.ts * 1000)).toISOString()
    next.current_source_ts = new Date(trade.ts * 1000).toISOString()
    next.current_source_lt =
      typeof trade.lt === 'number' && Number.isFinite(trade.lt) ? trade.lt : null
  }
  if (hasUSD) {
    next.current_price = usdCurrent
  } else if (hasNative) {
    // The browser has no request-time FX fetch. Carry only the last proven
    // current FX rate, never the previous USD absolute beside a newer native
    // price; without such a mark, leave USD honestly absent.
    next.current_price = priorCurrentFX == null ? null : (nativePrice as number) * priorCurrentFX
  }
  if (hasNative) {
    next.current_native_price = nativePrice as number
  }

  const usdPct = priceChangePct(next.baseline_price, next.current_price)
  const nativePct = priceChangePct(next.baseline_native_price, next.current_native_price)
  const pct =
    next.pct_denom === 'native'
      ? nativePct
      : next.pct_denom === 'usd'
        ? usdPct
        : (usdPct ?? nativePct)
  if (pct != null) {
    next.pct = pct
    // Lifecycle status is semantic evidence and must survive live updates.
    // Every other usable baseline is an ordinary complete window.
    next.status =
      window.status === 'since_birth' ||
      window.status === 'since_first_trade' ||
      window.status === 'since_recovery'
        ? window.status
        : 'ok'
  }
  return next
}

function priceChangePct(
  baseline: number | null | undefined,
  current: number | null | undefined
): number | null {
  if (
    typeof baseline !== 'number' ||
    !Number.isFinite(baseline) ||
    baseline <= 0 ||
    typeof current !== 'number' ||
    !Number.isFinite(current)
  ) {
    return null
  }
  return ((current - baseline) / baseline) * 100
}

export function stalePairPriceWindows(
  pair: PairDetail,
  trade?: TradeSummary,
  nativePrice?: number
): PairDetail {
  const next: PairDetail = {
    ...pair,
    price_change_5m_pct: null,
    price_change_1h_pct: null,
    price_change_6h_pct: null,
    price_change_24h_pct: null,
  }
  if (next.price_windows) {
    next.price_windows = Object.fromEntries(
      Object.entries(next.price_windows).map(([key, window]) => [
        key,
        stalePriceWindow(window, trade, nativePrice),
      ])
    )
  }
  return next
}

export function stalePriceWindow(
  window: PriceWindowSnapshot,
  trade?: TradeSummary,
  nativePrice?: number
): PriceWindowSnapshot {
  const next: PriceWindowSnapshot = { ...window, pct: null, status: 'stale' }
  if (!trade || !Number.isFinite(trade.ts)) return next
  next.current_source_ts = new Date(trade.ts * 1000).toISOString()
  next.current_source_lt =
    typeof trade.lt === 'number' && Number.isFinite(trade.lt) ? trade.lt : null
  if (typeof trade.price_usd === 'number' && Number.isFinite(trade.price_usd)) {
    next.current_price = trade.price_usd
  } else {
    const usdCurrent = tradeWindowUsdPrice(trade, nativePrice)
    if (usdCurrent != null) next.current_price = usdCurrent
  }
  if (typeof nativePrice === 'number' && Number.isFinite(nativePrice)) {
    next.current_native_price = nativePrice
  }
  return next
}

// monotonicMax folds a server-side monotonic/cumulative stat: the event's value
// wins only when it is a finite number ABOVE the current one; an omitted field
// or an out-of-order lower value leaves the page's value untouched. Pure.
export function monotonicMax(
  prevVal: number | null | undefined,
  eventVal: number | null | undefined
): number | null | undefined {
  if (typeof eventVal !== 'number' || !Number.isFinite(eventVal)) return prevVal
  return Math.max(eventVal, typeof prevVal === 'number' ? prevVal : 0)
}

// foldTradeAth folds a trade frame's ATH (the server's ATH after that trade) into
// the page's (ATH-BIRTH, ADR-052 §5). Before any trade the page holds the primary
// pool's birth mcap (ath_source "birth"); the first frame that carries an ATH
// REPLACES it, even a lower one, and switches the source to "trades". From then
// on it is raise-only (monotonicMax). A frame without an ATH changes nothing. Pure.
/** The operator's ATH rule (2026-11-04): the ATH never stands below the
 *  current MC — a trade that lifts the MC above it raises it in the same tick
 *  (athpool.AtLeastMcap's twin). An unknown ATH stays unknown. */
export function athAtLeastMcap(
  ath: number | null | undefined,
  mcap: number | null | undefined
): number | null | undefined {
  if (typeof ath !== 'number' || !Number.isFinite(ath)) return ath
  return typeof mcap === 'number' && Number.isFinite(mcap) && mcap > ath ? mcap : ath
}

export function foldTradeAth(
  prev: Pick<PairDetail, 'ath_mcap_usd' | 'ath_source'>,
  eventVal: number | null | undefined
): Pick<PairDetail, 'ath_mcap_usd' | 'ath_source'> {
  if (typeof eventVal !== 'number' || !Number.isFinite(eventVal) || eventVal <= 0) {
    return { ath_mcap_usd: prev.ath_mcap_usd, ath_source: prev.ath_source }
  }
  if (prev.ath_source === 'birth') return { ath_mcap_usd: eventVal, ath_source: 'trades' }
  return { ath_mcap_usd: monotonicMax(prev.ath_mcap_usd, eventVal), ath_source: 'trades' }
}

// applyLiquidityUpdate folds a LiquidityUpdateEvent (LP add/remove) onto the cached pair.
// A finite number — INCLUDING 0 — sets the Liquidity cell, so a fully-withdrawn pool shows
// "$0" LIVE (not "—" only on the next REST refresh) and any LP add/remove moves the cell the
// way a trade does. A null/undefined (the quote is unpriceable or the reserve is unknown,
// == REST) blanks the cell to "—". Pure. ADR-051.
export function applyLiquidityUpdate(
  prev: PairDetail,
  liquidityUsd: number | null | undefined
): PairDetail {
  const known = typeof liquidityUsd === 'number' && Number.isFinite(liquidityUsd)
  return { ...prev, liquidity_usd: known ? liquidityUsd : null }
}

// (mergeLiveDetail was removed in FRONTEND-WS-MCAP Part 3 — the 3s detail poll it served
// is gone; Activity is a client-side window and slow fields refresh on the reconnect re-seed.)
